import React, { useEffect, useLayoutEffect, useMemo, useRef, useState, useTransition } from "react";
import { useDispatch, useSelector } from "react-redux";
import { callSwitchAccountApi } from "../../Context/switchAccountApi";
import { onSavePoints } from "../../Store/Slices/cardDataSlice";
import getTranslationData from "../../TranslationUtility/TranslationUtility";
import cardPlaceholder from "../../Assets/IDFC UAT - Credit card default image.png";
import defaultImage from "../../Assets/IDFC UAT DC - Default image.svg";
import {
  calculateEarning,
  formatPrice,
  getRewardBaseDenomination,
  LoginV3Data,
  trackEvent,
  useEarnRoiCalculationContext,
  useGaTrackingConfig,
} from "../Utility/Utility";
import pointIcon from "../../Assets/point_icon.svg";
import {
  calculatePercentagePoints,
  getRewardRoundingSettings,
} from "./utils/rewardRounding";
import { getMinimumRewardingOrderEligibility } from "./utils/rewardEligibility";

const number = (value) => Number(value || 0);
const isEnabled = (value) => value === true || value === 1 || String(value).toLowerCase() === "true" || String(value) === "1";
const hasConfigValue = (value) => value !== undefined && value !== null && String(value).trim() !== "";
const calculateBurnLimit = ({
  enabled,
  type,
  value,
  percentageBase,
  fallback,
  roundMethod,
  roundDigits,
}) => {
  if (!enabled) return fallback;

  const normalizedType = String(type || "fixed").trim().toLowerCase();
  const normalizedValue = Number(String(value ?? 0).replace("%", "").trim()) || 0;

  if (normalizedType !== "percentage") return normalizedValue;

  return calculatePercentagePoints({
    percentageBase,
    percentage: normalizedValue,
    roundMethod,
    roundDigits,
  });
};

function RewardRedemption({
  cards,
  allCards,
  selectedCard,
  selectedCardId,
  setSelectedCardId,
  setSelectedCard,
  cardBalances,
  setCardBalances,
  matchedCardMasterData,
  isEarnEnabled,
  isBurnEnabled,
  isClientEarnEnabled,
  isClientBurnEnabled,
  productSupportsEarn,
  productSupportsBurn,
  effectiveEarn,
  effectiveBurn,
  clientConfigurations,
  productReducer,
  productData,
  updatedData,
  convenienceFee,
  gst,
  points,
  setPoints,
  setBurnPoints,
  setCash,
  onTotalPayableAmountChange,
  setEarnPoints,
}) {
  const translation = getTranslationData();
  const dispatch = useDispatch();
  const clientBurnConfig = useSelector(
    (state) => state.clientConfigurationReducer?.clientConfigurationData?.[0],
  );
  const earnRoiCalculationContext = useEarnRoiCalculationContext({
    selectedLmsCard: selectedCard,
    cardMasterConfiguration: matchedCardMasterData,
  });
  const loginV3Data = LoginV3Data();
  const [redemptionOn, setRedemptionOn] = useState(false);
  const [selectorOpen, setSelectorOpen] = useState(false);
  const [pointsInput, setPointsInput] = useState(String(number(points)));
  const [sliderValue, setSliderValue] = useState(null);
  const [isSliderPending, startSliderTransition] = useTransition();
  const editingPointsRef = useRef(false);
  const previousMinimumRef = useRef(null);
  const pointsRef = useRef(number(points));
  const selectorRef = useRef(null);
  const payableChangeRef = useRef(onTotalPayableAmountChange);
  const quantitySignature = (updatedData || [])
    .map((item) => `${item?.denomination}:${number(item?.quantity)}`)
    .join("|");
  const previousQuantitySignatureRef = useRef(quantitySignature);
  pointsRef.current = number(points);
  const gaTrackingConfig = useGaTrackingConfig();
  useEffect(() => {
    payableChangeRef.current = onTotalPayableAmountChange;
  }, [onTotalPayableAmountChange]);

  const balance = number(cardBalances?.[selectedCardId]?.points);
  const basePayable = useMemo(
    () => (updatedData || []).reduce(
      (sum, item) => sum + number(item?.you_pay) * number(item?.quantity),
      0,
    ),
    [updatedData],
  );
  const totalPayable = useMemo(
    () => (
      basePayable + number(convenienceFee)
      + number(gst)
    ),
    [basePayable, convenienceFee, gst],
  );
  const {
    hasMinimumValue: hasMinimumRewardingOrderValue,
    isEligible: isMinimumRewardingOrderEligible,
    minimumValue: minimumRewardingOrderValue,
  } = getMinimumRewardingOrderEligibility({
    checkoutValue: totalPayable,
    minimumRewardingOrderValue:
      matchedCardMasterData?.minimumRewardingOrderValue,
  });
  const denominationConfigs =
    productReducer?.productReducerDisocount?.clientProductDenomination || [];
  const minPrice = getRewardBaseDenomination({
    product: productData,
    // Earn and worth conversion use the configured minimum denomination,
    // regardless of whether that denomination can currently be selected.
    eligibleDenominations: denominationConfigs,
    fallbackDenominations: denominationConfigs,
  }) || 1;
  const roundingSettings = getRewardRoundingSettings(
    clientBurnConfig?.pointRoundingConfiguration,
  );
  const roundMethod = roundingSettings?.roundMethod;
  const roundDigits = roundingSettings?.roundDigits;
  const earning = calculateEarning({
    ...earnRoiCalculationContext,
    amount: totalPayable,
    minEarnPoints: productData?.minEarnPoints,
    minBurnPoints: productData?.maxEarnPoints,
    baseDenomination: minPrice,
    burnRoundMethod: roundMethod,
    burnRoundDigits: roundDigits,
  });
  const earnedPoints =
    effectiveEarn && isMinimumRewardingOrderEligible ? earning.points : 0;
  const productSupportsNeither = !productSupportsEarn && !productSupportsBurn;
  const isBurnAvailableForOrder = effectiveBurn;
  const isEarnAvailableForOrder = effectiveEarn;
  // Burn conversion: Min Burn Points / configured product minimum denomination.
  const pointsValueConversionRate = earning.burnRate;
  const burnConversionRate = effectiveBurn
    ? pointsValueConversionRate
    : 0;
  const orderTotalValue = burnConversionRate > 0
    ? totalPayable * burnConversionRate
    : 0;
  const orderPointsMaximum = calculateBurnLimit({
    enabled: true,
    type: "percentage",
    value: 100,
    percentageBase: orderTotalValue,
    fallback: 0,
    roundMethod,
    roundDigits,
  });

  const getConfig = (key) => clientConfigurations?.find(
    (item) => String(item?.configKey || "").toLowerCase() === key,
  )?.configValue;
  const resolveClientConfig = (cardKey, clientKey) => {
    const values = [
      clientBurnConfig?.[cardKey],
      getConfig(clientKey),
    ];
    return values.find(hasConfigValue);
  };
  const resolveBurnRule = (prefix) => {
    const isMinimum = prefix === "min";
    const enableKey = isMinimum ? "enableMinBurn" : "enableMaxBurn";
    const typeKey = isMinimum ? "minBurnType" : "maxBurnType";
    const valueKey = isMinimum ? "minBurnValue" : "maxBurnValue";
    const cardRuleValue = Number(
      String(matchedCardMasterData?.[valueKey] ?? 0).replace("%", "").trim(),
    );
    const cardHasRule = isEnabled(matchedCardMasterData?.[enableKey])
      && hasConfigValue(matchedCardMasterData?.[typeKey])
      && Number.isFinite(cardRuleValue)
      && cardRuleValue > 0;

    return cardHasRule
      ? {
        enabled: matchedCardMasterData[enableKey],
        type: matchedCardMasterData[typeKey],
        value: matchedCardMasterData[valueKey],
      }
      : {
        enabled: resolveClientConfig(enableKey, enableKey.toLowerCase()),
        type: resolveClientConfig(typeKey, typeKey.toLowerCase()),
        value: resolveClientConfig(valueKey, valueKey.toLowerCase()),
      };
  };
  const minRule = effectiveBurn ? resolveBurnRule("min") : null;
  const maxRule = effectiveBurn ? resolveBurnRule("max") : null;
  // Apply rounding only after each percentage threshold is calculated.
  const minimum = effectiveBurn
    ? calculateBurnLimit({
      enabled: isEnabled(minRule.enabled),
      type: minRule.type,
      value: minRule.value,
      percentageBase: orderTotalValue,
      fallback: 0,
      roundMethod,
      roundDigits,
    })
    : 0;
  const configuredMaximum = effectiveBurn
    ? calculateBurnLimit({
      enabled: isEnabled(maxRule.enabled),
      type: maxRule.type,
      value: maxRule.value,
      percentageBase: orderTotalValue,
      fallback: orderPointsMaximum,
      roundMethod,
      roundDigits,
    })
    : 0;
  const maximum = effectiveBurn
    ? Math.min(balance, configuredMaximum, orderPointsMaximum)
    : 0;
  // When no minimum rule exists, redemption starts at zero and remains usable.
  // The maximum defaults to the full ROI-derived order points (capped by balance).
  const hasRedeemablePoints = balance > 0 && maximum > 0;
  const minimumExceedsOrderPoints =
    isBurnAvailableForOrder && minimum > 0 && minimum > orderPointsMaximum;
  const insufficient =
    isBurnAvailableForOrder &&
    minimum > 0 &&
    balance < minimum;
  const redemptionDisabled =
    !hasRedeemablePoints || insufficient || minimumExceedsOrderPoints || maximum < minimum;
  const isRedeeming = isBurnAvailableForOrder && !redemptionDisabled && redemptionOn;

  const displayPoints = sliderValue ?? number(points);
  const earnedValue =
    effectiveEarn && isMinimumRewardingOrderEligible ? earning.worth : 0;
  const savings = earning.burnRoi > 0
    ? Math.min(totalPayable, displayPoints * earning.burnRoi)
    : 0;

  // Once the checkout has caught up with the slider, show the committed points again.
  useEffect(() => {
    if (!isSliderPending) setSliderValue(null);
  }, [isSliderPending]);

  useEffect(() => {
    setEarnPoints?.(earnedPoints);
  }, [earnedPoints, setEarnPoints]);

  useEffect(() => {
    setRedemptionOn(false);
    setSelectorOpen(false);
  }, [selectedCardId]);

  useEffect(() => {
    const closeOnOutsideClick = (event) => {
      if (selectorRef.current && !selectorRef.current.contains(event.target)) setSelectorOpen(false);
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    return () => document.removeEventListener("mousedown", closeOnOutsideClick);
  }, []);

  const applyPoints = (value) => {
    const selected = Math.max(minimum, Math.min(number(value), maximum));
    const remaining = Math.max(
      0,
      totalPayable - (earning.burnRoi > 0 ? selected * earning.burnRoi : 0),
    );
    setPoints(selected);
    setBurnPoints(selected);
    setCash(Number(remaining.toFixed(2)));
    onTotalPayableAmountChange(remaining);
    return selected;
  };

  useLayoutEffect(() => {
    if (!editingPointsRef.current) setPointsInput(String(number(points)));
  }, [points]);

  const commitPointsInput = () => {
    editingPointsRef.current = false;
    const selected = applyPoints(pointsInput === "" ? minimum : pointsInput);
    setPointsInput(String(selected));
  };

  const handleSliderChange = (event) => {
    editingPointsRef.current = false;
    // Same clamp as applyPoints, applied immediately for the thumb and input.
    const selected = Math.max(minimum, Math.min(number(event.target.value), maximum));
    setSliderValue(selected);
    setPointsInput(String(selected));
    startSliderTransition(() => {
      applyPoints(selected);
    });
  };

  const clearRedemption = () => {
    setPoints(0);
    setBurnPoints(0);
    setCash(Number(totalPayable.toFixed(2)));
    onTotalPayableAmountChange(totalPayable);
  };

  useLayoutEffect(() => {
    const previousMinimum = previousMinimumRef.current;
    previousMinimumRef.current = minimum;
    const quantityChanged =
      previousQuantitySignatureRef.current !== quantitySignature;
    previousQuantitySignatureRef.current = quantitySignature;

    if (!isBurnAvailableForOrder || !redemptionOn) return;
    if (redemptionDisabled) {
      setRedemptionOn(false);
      setPoints(0);
      setBurnPoints(0);
      setCash(Number(totalPayable.toFixed(2)));
      payableChangeRef.current(totalPayable);
      return;
    }

    const currentPoints = pointsRef.current;
    if (currentPoints <= 0 && minimum > 0) {
      setRedemptionOn(false);
      setCash(Number(totalPayable.toFixed(2)));
      payableChangeRef.current(totalPayable);
      return;
    }
    const wasAtPreviousMinimum =
      previousMinimum !== null && currentPoints === previousMinimum;
    const selected = quantityChanged || wasAtPreviousMinimum
      ? minimum
      : Math.max(minimum, Math.min(currentPoints, maximum));
    const remaining = Math.max(
      0,
      totalPayable - (earning.burnRoi > 0 ? selected * earning.burnRoi : 0),
    );
    if (selected !== currentPoints) {
      setPoints(selected);
      setBurnPoints(selected);
    }
    setCash(Number(remaining.toFixed(2)));
    payableChangeRef.current(remaining);
  }, [
    selectedCardId,
    quantitySignature,
    isBurnAvailableForOrder,
    redemptionOn,
    redemptionDisabled,
    points,
    minimum,
    maximum,
    totalPayable,
    burnConversionRate,
    earning.burnRoi,
    setPoints,
    setBurnPoints,
    setCash,
  ]);

  const handleToggle = () => {
    if (!isBurnAvailableForOrder || redemptionDisabled) return;
    const next = !redemptionOn;
    setRedemptionOn(next);
    if (next) applyPoints(minimum);
    else clearRedemption();
  };

  const fetchCardBalance = async (card) => {
    const identifier = card?.identifier;
    setCardBalances((previous) => ({ ...previous, [identifier]: { ...previous?.[identifier], loading: true, failed: false } }));
    try {
      const response = await callSwitchAccountApi({
        identifier: loginV3Data?.memberIdentifier,
        subIdentifier: identifier,
        identifierType: card?.identifiertype ? JSON.parse(card.identifiertype) : 1002,
        isAccountSwitch: true,
        userSessionToken: sessionStorage.getItem("loginSessionToken"),
      });
      const cardPoints = Math.floor(response?.response?.response?.memberBalance?.pointBalance ?? 0);
      setCardBalances((previous) => ({ ...previous, [identifier]: { loading: false, failed: response?.errorCode !== "200", points: cardPoints } }));
      if (response?.errorCode === "200") {
        dispatch(onSavePoints({ pointBalance: cardPoints, identifierType: card?.identifiertype, subIdentifier: identifier }));
      }
    } catch {
      setCardBalances((previous) => ({ ...previous, [identifier]: { loading: false, failed: true, points: null } }));
    }
  };

  const chooseCard = (card) => {
    if (!card?.identifier || card.identifier === selectedCardId) {
      setSelectorOpen(false);
      return;
    }
    clearRedemption();
    setSelectedCardId(card.identifier);
    setSelectedCard(card);
    const identifier = String(card.identifier);
    if (sessionStorage.getItem("selectedCardId") !== identifier) {
      sessionStorage.setItem("selectedCardId", identifier);
    }
    if (localStorage.getItem("selectedCardId") !== identifier) {
      localStorage.setItem("selectedCardId", identifier);
    }
    // Publish checkout's selection immediately so the read-only card header
    // follows it even when the subsequent balance request fails.
    dispatch(onSavePoints({
      pointBalance: 0,
      identifierType: card?.identifiertype,
      subIdentifier: identifier,
    }));
    fetchCardBalance(card);
    setSelectorOpen(false);
    trackEvent("select_bank_card", {
      page_type: "checkout_page",
      // ...gaTrackingConfig.trackingContext,
      client_code: gaTrackingConfig.trackingContext.client_code,
      platform_code: gaTrackingConfig.trackingContext.platform_code,
      card_name: card.CardTypeName,
      reward_points: cardBalances?.[card.identifier]?.points ?? 0,
      page_url: window.location.href,

    });
  };

  const cardImage = (card) => {
    const imageLink =
      card?.backgroundcolor
      || card?.backgroundcolor;

    if (!imageLink) return null;
    if (/^(https?:)?\/\//i.test(imageLink) || String(imageLink).startsWith("data:")) {
      return String(imageLink).replace(/ /g, "%20");
    }
    return `${process.env.REACT_APP_CLIENT_URL}${imageLink}`.replace(/ /g, "%20");
  };

  const cardLogoClass = (card, fallbackIndex = 0) => {
    const originalIndex = allCards?.findIndex(
      (item) => String(item?.identifier) === String(card?.identifier),
    );
    const stableIndex = originalIndex >= 0 ? originalIndex : fallbackIndex;
    return `rewards__card-logo${stableIndex % 2 === 1 ? " rewards__card-logo--maroon" : ""}`;
  };

  const insufficientFallback = `Minimum ${minimum.toLocaleString()} points required. Try a different card.`;
  const insufficientTemplate = translation?.checkoutInsufficientBalance;
  const insufficientMessage = insufficientTemplate?.includes("{minimum}")
    ? insufficientTemplate.replace("{minimum}", minimum.toLocaleString())
    : insufficientFallback;
  const translatedSelectCard = translation?.selectCard || "Select Card";
  const selectCardLabel = String(translatedSelectCard).trim().toUpperCase() === "SELECT CARD"
    ? "Select Card"
    : translatedSelectCard;
  const orderUnavailableFallback =
    "Earning or redemption is not available for this order.";
  const translatedOrderUnavailable =
    translation?.checkoutOrderEarnRedeemUnavailable;
  const translatedCardUnsupported =
    translation?.checkoutEarnRedeemUnsupported;
  const orderUnavailableCopy =
    translatedOrderUnavailable &&
      String(translatedOrderUnavailable).trim() !==
      String(translatedCardUnsupported || "").trim()
      ? translatedOrderUnavailable
      : orderUnavailableFallback;

  // English fallbacks keep the panel usable if translation content is unavailable.
  const copy = {
    redeem: translation?.checkoutRedeemPoints || "Redeem Points",
    earn: translation?.checkoutEarnPoints || "Earn Points",
    hint: translation?.checkoutEarnRedeemHint || "One benefit per order, earn or redeem",
    save: translation?.checkoutYouSave || "You save",
    minimum: translation?.checkoutMinimum || "Min",
    maximum: translation?.checkoutMaximum || "Max",
    willEarn: translation?.checkoutWillEarn || "You’ll earn",
    worth: translation?.checkoutWorth || "worth",
    insufficient: insufficientMessage,
    noEarn: translation?.checkoutEarnUnsupported || "This card does not support earning. Please select a different card.",
    noRedeem: translation?.checkoutRedeemUnsupported || "This card does not support redemption. Please select a different card.",
    orderNeither: orderUnavailableCopy,
    selectCard: selectCardLabel,
    minimumRewardNotMet: translation?.checkoutMinimumRewardNotMet || "You won’t earn {points} on this purchase.",
    estimatedReward: translation?.checkoutEstimatedReward || "Estimated amount based on max redemption value.",
    minimumRewardOrder: translation?.checkoutMinimumRewardOrder || "Rewards apply to orders of {amount} or more.",
  };

  const minimumRewardOrderMessage = copy.minimumRewardOrder.includes("{amount}")
    ? copy.minimumRewardOrder.replace(
      "{amount}",
      `₹${minimumRewardingOrderValue.toLocaleString("en-IN")}`,
    )
    : `${copy.minimumRewardOrder} ₹${minimumRewardingOrderValue.toLocaleString("en-IN")}`;
  const minimumRewardNotMetParts = copy.minimumRewardNotMet.split("{points}");

  const selectedCardIsVisible = cards?.some(
    (card) => card?.identifier === selectedCard?.identifier,
  );
  if (!selectedCard || !cards?.length || !selectedCardIsVisible) return null;
  const selectedCardIndex = cards.findIndex(
    (card) => card?.identifier === selectedCard?.identifier,
  );
  const showRedemptionToggle = isBurnAvailableForOrder;
  const toggleIsOn =
    isBurnAvailableForOrder && !redemptionDisabled && redemptionOn;
  const neitherSupported = !isEarnAvailableForOrder && !isBurnAvailableForOrder;
  const showEarnRedeemHint =
    isClientEarnEnabled &&
    isClientBurnEnabled &&
    isEarnAvailableForOrder &&
    isBurnAvailableForOrder;
  const clientIsEarnOnly = isClientEarnEnabled && !isClientBurnEnabled;
  const clientIsRedeemOnly = !isClientEarnEnabled && isClientBurnEnabled;
  let unsupportedMessage = "";
  if (productSupportsNeither) unsupportedMessage = copy.orderNeither;
  else if (clientIsEarnOnly && !isEarnEnabled) unsupportedMessage = copy.noEarn;
  else if (clientIsRedeemOnly && !isBurnEnabled) unsupportedMessage = copy.noRedeem;
  else if (neitherSupported) unsupportedMessage = copy.orderNeither;

  return (
    <section className="rewards" ref={selectorRef}>
      <button type="button" className="rewards__card" onClick={() => setSelectorOpen((open) => !open)} aria-expanded={selectorOpen}>
        <span className="rewards__identity">
          {/* {cardImage(selectedCard) ? <img className={cardLogoClass(selectedCard, selectedCardIndex)} src={cardImage(selectedCard)} alt="" decoding="async" /> : <span className="rewards__card-mark"><span>{selectedCard?.CardTypeName || "CC"}</span></span>} */}
          {cardImage(selectedCard) ? <img className={cardLogoClass(selectedCard, selectedCardIndex)} src={cardImage(selectedCard)} alt="" decoding="async" onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = cardPlaceholder; }} /> :
            <img className={cardLogoClass(selectedCard, selectedCardIndex)} src={cardPlaceholder} alt="" decoding="async" onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = defaultImage; }} />
            // <span className="rewards__card-mark"><span>{selectedCard?.CardTypeName || "CC"}</span></span>
          }
          <span>{selectedCard?.LogoDescription} ••{selectedCard?.CardNoLast4Digit}</span>
        </span>
        <span className="rewards__balance"><img className="rewards__coin" src={pointIcon} alt="" aria-hidden="true" /> {balance.toLocaleString()} <span className="rewards__chevron">⌄</span></span>
      </button>

      {selectorOpen && (
        <>
          <button className="rewards__scrim" type="button" aria-label="Close card selection" onClick={() => setSelectorOpen(false)} />
          <div className="rewards__selector" role="dialog" aria-label={copy.selectCard}>
            <p>{copy.selectCard}</p>
            <div className="rewards__selector-list">
              {cards.map((card, index) => (
                <button key={card.identifier} type="button" className={card.identifier === selectedCardId ? "is-selected" : ""} onClick={() => chooseCard(card)}>
                  <span className="rewards__identity">
                    {cardImage(card) ? <img className={cardLogoClass(card, index)} src={cardImage(card)} alt="" loading="lazy" decoding="async" onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = cardPlaceholder; }} /> :
                      <img className={cardLogoClass(card, index)} src={cardPlaceholder} alt="" loading="lazy" decoding="async" onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = defaultImage; }} />
                    }

                    {/* {cardImage(card) ? <img className={cardLogoClass(card, index)} src={cardImage(card)} alt="" loading="lazy" decoding="async" /> : <span className="rewards__card-mark"><span>{card?.CardTypeName || "CC"}</span></span>} */}
                    <span className="rewards__selector-details"><strong className="rewards__selector-name">{card.CardTypeName} •• {card.CardNoLast4Digit}</strong><small className="rewards__selector-balance"><img className="rewards__coin" src={pointIcon} alt="" aria-hidden="true" /> <span className="rewards__selector-points">{number(cardBalances?.[card.identifier]?.points).toLocaleString("en-IN")}</span></small></span>
                  </span>
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      <>
        {isBurnAvailableForOrder && (
          <div className="rewards__redeem-container">
            <div className="rewards__heading">
              <span>
                <strong>{copy.redeem}</strong>
                {showEarnRedeemHint && !insufficient && <small>{copy.hint}</small>}
                {insufficient && <small className="rewards__message--insufficient">{copy.insufficient}</small>}
              </span>
              {showRedemptionToggle && <button type="button" className={`rewards__toggle${toggleIsOn ? " is-on" : ""}`} role="switch" aria-checked={toggleIsOn} onClick={handleToggle} disabled={redemptionDisabled}><span /></button>}
            </div>

            {isRedeeming && (
              <div className="rewards__redemption">
                <div className="rewards__selected">
                  <label className="rewards__points-input">
                    <img className="rewards__coin" src={pointIcon} alt="" aria-hidden="true" />
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9,]*"
                      aria-label={copy.redeem}
                      value={pointsInput === "" ? "" : number(pointsInput).toLocaleString("en-IN")}
                      onFocus={() => { editingPointsRef.current = true; }}
                      onChange={(event) => {
                        const numericValue = event.target.value.replace(/,/g, "");
                        if (/^\d*$/.test(numericValue)) setPointsInput(numericValue);
                      }}
                      onBlur={commitPointsInput}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") event.currentTarget.blur();
                      }}
                    />
                  </label>
                  {burnConversionRate > 0 && (
                    <span>{copy.save} <b>₹{formatPrice(savings)}</b></span>
                  )}
                </div>
                <input type="range" min={minimum} max={maximum} value={Math.max(minimum, displayPoints)} onChange={handleSliderChange} style={{ "--range": `${maximum === minimum ? 100 : ((displayPoints - minimum) / (maximum - minimum)) * 100}%` }} />
                <div className="rewards__limits">
                  <span>
                    <img className="rewards__coin" src={pointIcon} alt="" aria-hidden="true" />
                    <b className="rewards__limit-value">{minimum.toLocaleString("en-IN")}</b>
                    <span className="rewards__limit-label"><span className="bracetsToggle">(</span>{copy.minimum}<span className="bracetsToggle">)</span></span>
                  </span>
                  <span>
                    <img className="rewards__coin" src={pointIcon} alt="" aria-hidden="true" />
                    <b className="rewards__limit-value">{maximum.toLocaleString("en-IN")}</b>
                    <span className="rewards__limit-label"><span className="bracetsToggle">(</span>{copy.maximum}<span className="bracetsToggle">)</span></span>
                    <span className="rewards__info" aria-hidden="true">i</span>
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        {unsupportedMessage && <p className={`rewards__message${neitherSupported ? " rewards__message--unsupported" : ""}`}>{unsupportedMessage}</p>}
        {isEarnAvailableForOrder && !isBurnAvailableForOrder && !isRedeeming && (
          <div className="rewards__earn-heading">
            <strong>{copy.earn}</strong>
          </div>
        )}
        {isEarnAvailableForOrder && !isRedeeming && (
          <div className="rewards__earn-container">
            {isMinimumRewardingOrderEligible ? (
              <>
                <p className="reward_earn_point_text">
                  {copy.willEarn}{" "}
                  <img className="rewards__coin" src={pointIcon} alt="" aria-hidden="true" />{" "}
                  <b className="rewards__earned-points">{earnedPoints.toLocaleString()}</b>
                  {earnedValue > 0 && (
                    <> {copy.worth} <b>₹{formatPrice(earnedValue)}</b></>
                  )}
                </p>
                {hasMinimumRewardingOrderValue && (
                  <p className="rewards__earn-note">
                    <span className="rewards__note-asterisk">*</span>
                    {copy.estimatedReward}
                  </p>
                )}
              </>
            ) : (
              <p
                className="reward_earn_point_text rewards__earn-ineligible"
                aria-label={copy.minimumRewardNotMet.replace("{points}", "reward points")}
              >
                {minimumRewardNotMetParts[0]}
                {minimumRewardNotMetParts.length > 1 && (
                  <img className="rewards__coin" src={pointIcon} alt="" aria-hidden="true" />
                )}
                {minimumRewardNotMetParts.slice(1).join("{points}")}
              </p>
            )}
            {hasMinimumRewardingOrderValue && (
              <p className="rewards__earn-note">
                <span className="rewards__note-asterisk">*</span>
                {minimumRewardOrderMessage}
              </p>
            )}
          </div>
        )}
      </>
    </section>
  );
}

export default RewardRedemption;
